import React, { Fragment, useCallback, useEffect, useState } from 'react';
import {
  IconButton,
  makeStyles,
  Button,
  CheckIcon,
  AddIcon,
  useSnackbar,
  Alert,
  AlertTitle,
  Tooltip,
  CloseIcon,
  LaunchIcon,
  CircularProgress,
  useMediaQuery,
} from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import { getIsAssetOwned, postPurchaseAsset } from '@modules/utilities/utils/assetUtils';
import { useRobloxAuthentication } from '@rbx/auth';
import TryInStudioDialog from '../shared/components/TryInStudioDialog';

export type TCodeSnippetProps = {
  assetId: number;
  title?: string;
  'aria-add-to-inventory-label'?: string;
};

const useCodeSnippetStyles = makeStyles()(() => ({
  iconButton: {
    marginLeft: '4px',
    marginRight: '4px',
  },
  divider: {
    margin: 5,
  },
  alertButton: {
    paddingTop: '-8px',
  },
  closeIconButton: {
    marginLeft: '4px',
  },
  circularProgress: {
    marginLeft: '4px',
    marginRight: '4px',
    marginTop: '8px',
  },
}));

const RunnableCodeButton = (props: TCodeSnippetProps) => {
  const { assetId, title, 'aria-add-to-inventory-label': ariaAddToInventoryLabel } = props;
  const {
    classes: { iconButton, alertButton, closeIconButton, circularProgress },
  } = useCodeSnippetStyles();
  const [isAssetOwned, setIsAssetOwned] = useState<boolean>(false);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [isTryInStudioOpen, setIsTryInStudioOpen] = useState(false);
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const { user: authenticatedUser, login } = useRobloxAuthentication();
  const { translate } = useTranslation();
  const { enqueue, close: closeSnackbar } = useSnackbar();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('XSmall'));

  const handleOpenInStudioButtonClick = useCallback(async () => {
    setIsTryInStudioOpen(true);
  }, []);

  const showAddToInventorySnackbar = useCallback(
    (msg: string, isSuccess: boolean, errorTitle?: string) => {
      if (isSuccess) {
        enqueue({
          children: (
            <Alert
              severity='success'
              variant='filled'
              action={[
                <Button
                  color='inherit'
                  size='small'
                  key='openStudio'
                  classes={{ root: alertButton }}
                  onClick={handleOpenInStudioButtonClick}>
                  {translate('Button.OpenStudio')}
                </Button>,
                <IconButton
                  color='inherit'
                  classes={{ root: closeIconButton }}
                  aria-label={translate('Label.CloseAlertButton')}
                  size='small'
                  key='alertCloseButton'
                  onClick={closeSnackbar}>
                  <CloseIcon fontSize='small' />
                </IconButton>,
              ]}>
              {msg}
            </Alert>
          ),
          anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
          autoHide: true,
          onClose: closeSnackbar,
        });
      } else {
        enqueue({
          children: (
            <Alert severity='error' variant='filled'>
              <AlertTitle>{errorTitle}</AlertTitle>
            </Alert>
          ),
          autoHide: true,
          onClose: closeSnackbar,
        });
      }
    },
    [
      enqueue,
      closeSnackbar,
      alertButton,
      closeIconButton,
      handleOpenInStudioButtonClick,
      translate,
    ],
  );

  const handleAddToInventoryButtonClick = useCallback(async () => {
    setIsButtonLoading(true);
    if (userId === undefined) {
      await login();
    }
    const isAddedSuccess = await postPurchaseAsset(userId ?? 0, assetId);
    setIsButtonLoading(false);
    if (isAddedSuccess) {
      showAddToInventorySnackbar(
        title
          ? translate('Message.AddToInventorySuccessWithTitle', { title })
          : translate('Message.AddToInventorySuccess'),
        true,
      );
      setIsAssetOwned(true);
    } else {
      showAddToInventorySnackbar(
        translate('Message.AddToInventoryFailedMessage', { title: title ?? '' }),
        false,
        translate('Message.AddToInventoryFailedTitle'),
      );
    }
  }, [assetId, login, userId, showAddToInventorySnackbar, title, translate]);

  const onTryInStudioClosed = useCallback(() => {
    setIsTryInStudioOpen(false);
  }, []);

  const fetchData = useCallback(async () => {
    if (userId !== undefined) {
      const isCurrentAssetOwned = await getIsAssetOwned(userId, assetId);
      setIsAssetOwned(isCurrentAssetOwned);
    }
  }, [assetId, userId]);

  useEffect(() => {
    if (authenticatedUser !== undefined && authenticatedUser?.id !== undefined) {
      setUserId(authenticatedUser?.id);
    }
    fetchData();
  }, [authenticatedUser, fetchData]);

  const openInStudioButton = useCallback(
    (onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined) => {
      return (
        <IconButton
          classes={{ root: iconButton }}
          aria-label={translate('Button.OpenInStudio')}
          color='default'
          size='small'
          onClick={onClick}>
          <LaunchIcon fontSize='small' />
        </IconButton>
      );
    },
    [iconButton, translate],
  );

  const addToInventoryButton = useCallback(
    (isLoading: boolean, onClick?: React.MouseEventHandler<HTMLButtonElement> | undefined) => {
      return isLoading ? (
        <CircularProgress size={16} classes={{ root: circularProgress }} />
      ) : (
        <IconButton
          classes={{ root: iconButton }}
          aria-label={ariaAddToInventoryLabel || translate('Button.AddToInventory')}
          color='default'
          size='small'
          disabled={isAssetOwned}
          onClick={onClick}>
          {isAssetOwned ? <CheckIcon fontSize='small' /> : <AddIcon fontSize='small' />}
        </IconButton>
      );
    },
    [ariaAddToInventoryLabel, circularProgress, iconButton, isAssetOwned, translate],
  );

  return (
    <Fragment>
      {!isMobile && (
        <Tooltip title={translate('Button.OpenInStudio')}>
          {openInStudioButton(handleOpenInStudioButtonClick)}
        </Tooltip>
      )}
      <Tooltip
        title={
          isAssetOwned ? translate('Button.AddedToInventory') : translate('Button.AddToInventory')
        }>
        <span>{addToInventoryButton(isButtonLoading, handleAddToInventoryButtonClick)}</span>
      </Tooltip>
      <TryInStudioDialog
        userId={userId ?? 0}
        assetId={assetId}
        isOpen={isTryInStudioOpen}
        onClose={onTryInStudioClosed}
      />
    </Fragment>
  );
};
export default RunnableCodeButton;
